// NOVAPAY-116-R5 / R5A — express one-page checkout + sync NovaPay handoff.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CartV2Item } from "./cart-v2.js";
import { computeTotals } from "./cart-v2.js";
import { formatUah } from "./money-v2.js";
import { submitCheckoutV2 } from "./checkout-v2-client.js";
import {
  buyerTypeLabelUk,
  fetchBuyerProfilesV2,
  type BuyerProfilePublic,
} from "./buyer-profiles-v2.js";
import {
  evaluateCheckoutAvailabilityPolicy,
  formatAvailabilityLineReasons,
} from "./checkout-availability-policy-k18.js";
import { reserveOrderNumber } from "./checkout-reservation-v2.js";
import {
  filterPersonNameInput,
  filterPhoneInput,
  phoneLiveHint,
  validateCheckout,
  type CheckoutFieldErrors,
  type PaymentMethodChoice,
} from "./checkout-validation-v2.js";
import { evaluateCheckoutReadiness } from "./checkout-readiness-v2.js";
import { IsolatedFormField } from "./IsolatedFormField.js";
import { readCheckoutPrefill, writeCheckoutPrefill } from "./checkout-prefill-v2.js";
import { persistCustomerRuntimeProfile } from "./customer-runtime-bootstrap-v2.js";
import {
  readAddressBook,
  upsertSavedAddress,
  telegramUserId,
  type CustomerSavedAddressV2,
} from "./customer-profile-v2.js";
import {
  applyDeliveryProfileToCheckout,
  deliveryMethodShortLabel,
  deliveryMethodTitle,
  deliveryTypeFromMode,
  formatDeliveryDestinationLine,
} from "./delivery-profile-v2.js";
import {
  findDuplicateSavedAddress,
  formatUaPhoneDisplay,
  isCheckoutContactDeliveryComplete,
  readCheckoutSessionProfileId,
  resolveCheckoutDeliveryProfile,
  writeCheckoutSessionProfileId,
} from "./checkout-delivery-resolve-v2.js";
import { STORE_INFO } from "./store-info-v2.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import { t } from "./store-v2-ui-copy.js";
import {
  isActiveReorderSession,
  peekReorderSession,
  patchReorderSession,
} from "./reorder-v2.js";
import { CartProductFinderV2, type CartAddSearchHit } from "./CartProductFinderV2.js";
import { CanonicalThreeProductRecommendations } from "./CanonicalThreeProductRecommendations.js";
/** Checkout recs are CrossSellBlock (three-product canonical wrapper). */
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { CrossSellCandidate } from "./cross-sell-rules.js";
import { getBankPaymentPurpose, tryOpenNbuBankApp, type BankPaymentClientPayload } from "./bank-iban-payment-v2.js";
import { StoreV2Icon } from "./StoreV2Icon.js";
import { miniImageUrl } from "./product-media-v2.js";
import {
  enabledCustomerCheckoutPaymentMethods,
  isSingleBankIbanCheckout,
} from "./customer-payment-flags-v2.js";
import {
  type CustomerPaymentTiming,
} from "./customer-payment-timing-v2.js";
import { CommercePageShell, PrimaryCommerceAction, SecondaryCommerceAction } from "./commerce-ui-v2.js";
import { NovapaySandboxBadge } from "./NovapaySandboxBadge.js";
import {
  buildHandoffGoUrl,
  openNovapayHandoffSync,
  prepareNovapayHandoff,
} from "./novapay-handoff-v2.js";
import { emitEvent } from "./events-v2.js";
import { ReportErrorCta } from "./CustomerFeedbackSheet.js";
import {
  isCheckoutDeliverySelectionConsistent,
  isNovaPoshtaReferenceCheckoutEnabled,
  selectionToCheckoutPayload,
  validateNovaPoshtaSelection,
} from "./nova-poshta-reference-client.js";
import { NovaPoshtaDeliverySection } from "./NovaPoshtaDeliverySection.js";
import type { NovaPoshtaReferenceSelection } from "./nova-poshta-reference-v1.js";
import { readNpCheckoutDraft, writeNpCheckoutDraft, clearNpCheckoutDraft } from "./np-checkout-draft-v2.js";
import { CheckoutLegalAcceptance } from "./legal-acceptance-v2.js";
import { BankPaymentDetailsV2, merchantBankDetails } from "./BankPaymentDetailsV2.js";
import { CardBrandTrustBlock } from "./card-brand-trust-v2.js";
import { NovapayPaymentOptionLabel } from "./novapay-payment-marks-v2.js";
import { isNovapayCheckoutDegradedStaging } from "./novapay-staging-degraded-v2.js";
import { isNovapayNotLiveUi } from "./novapay-live-gate-v2.js";
import { useRegisterGlobalAppCloseDirty } from "@asiastyle/surface-ui";

const COMPILE_BANK_IBAN_ONLY =
  String(import.meta.env.VITE_CUSTOMER_PAYMENT_MODE ?? "").trim().toUpperCase() === "BANK_IBAN_ONLY";
const CUSTOMER_RELEASE_STAGE = Number(
  String(import.meta.env.VITE_CUSTOMER_RELEASE ?? "").match(/K(\d+)/i)?.[1] ?? "0",
);
/** K14 shipped two final CTAs; keep them on every later K-stage (K15+ must not hide them). */
const TWO_FINAL_ACTION_CTAS = CUSTOMER_RELEASE_STAGE >= 14;

export interface CheckoutV2ViewProps {
  lang: StoreV2Lang;
  sessionKey: string;
  items: CartV2Item[];
  promoCode: string | null;
  /** Existing cart SoT — quantity edits before STG/order creation only. */
  onInc?: (id: string) => void;
  onDec?: (id: string) => void;
  onRemove?: (id: string) => void;
  onAddSearchHit?: (hit: CartAddSearchHit) => void;
  recommendations?: CrossSellCandidate[];
  onAddRecommendation?: (product: CatalogV2Product) => void;
  onOpenRecommendation?: (product: CatalogV2Product) => void;
  onRegisterActionable?: (products: CatalogV2Product[]) => void;
  onRecommendationImpression?: (ids: string[]) => void;
  onRecommendationsRefresh?: () => void;
  canRefreshRecommendations?: boolean;
  recommendationsRefreshing?: boolean;
  recommendationsLoading?: boolean;
  onChangeCart?: () => void;
  onOpenLegalOffer?: () => void;
  onOpenLegalPrivacy?: () => void;
  /** BUYER-INFRA-R1 — optional deep-link to cabinet buyer profiles (does not block retail). */
  onOpenBuyerProfiles?: () => void;
  onSuccess: (
    orderId: string,
    status: string | undefined,
    displayNumber: string | undefined,
    paymentMethod: PaymentMethodChoice,
    meta?: {
      totalUah?: number;
      contactName?: string;
      contactPhone?: string;
      bankPayment?: BankPaymentClientPayload | null;
      bankSheetOpen?: boolean;
      paymentTiming?: CustomerPaymentTiming;
      autoBankHandoff?: boolean;
    },
  ) => void;
  onTrustedPrice?: (productId: string, unitPriceUah: number) => void;
}

type TouchedMap = Partial<Record<keyof CheckoutFieldErrors, boolean>>;

function simpleHash(input: string): string {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

function buildCheckoutFingerprint(input: {
  sessionKey: string;
  contactName: string;
  normalizedPhone: string | null;
  city: string;
  warehouse: string;
  paymentMethod: string;
  items: Array<{ id: string; qty: number; priceUah: number }>;
  promoCode: string | null;
}): string {
  return [
    input.sessionKey,
    input.contactName,
    input.normalizedPhone ?? "",
    input.city,
    input.warehouse,
    input.paymentMethod,
    input.items.map((i) => `${i.id}:${i.qty}:${i.priceUah}`).join("|"),
    input.promoCode ?? "",
  ].join("::");
}

/**
 * COMMERCE-LAUNCH-167A-R1 — idempotency fingerprint for placeOrder.
 * Excludes paymentMethod so delivery-only edits + payment flips do not mint a second order.
 * Cart + contact + delivery define one purchase attempt.
 */
function buildPlaceOrderIdempotencyFingerprint(input: {
  sessionKey: string;
  contactName: string;
  normalizedPhone: string | null;
  city: string;
  warehouse: string;
  items: Array<{ id: string; qty: number; priceUah: number }>;
  promoCode: string | null;
}): string {
  return [
    input.sessionKey,
    input.contactName,
    input.normalizedPhone ?? "",
    input.city,
    input.warehouse,
    input.items.map((i) => `${i.id}:${i.qty}:${i.priceUah}`).join("|"),
    input.promoCode ?? "",
  ].join("::");
}

const CHECKOUT_ATTEMPT_PREFIX = "asiastyle.store-v2.checkout-attempt.v1:";
/** One attempt id per Mini App session_key for the whole checkout visit. */
const CHECKOUT_SESSION_ATTEMPT_PREFIX = "asiastyle.store-v2.checkout-session-attempt.v1:";

/** Stable per fingerprint until success — network replay returns the same order. */
function getOrCreateCheckoutAttemptId(cartFingerprintHash: string): string {
  const key = `${CHECKOUT_ATTEMPT_PREFIX}${cartFingerprintHash}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing && /^[a-z0-9]+$/i.test(existing)) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
        : simpleHash(`${cartFingerprintHash}:${Date.now()}`);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return simpleHash(`${cartFingerprintHash}:local`);
  }
}

function getOrCreateCheckoutSessionAttemptId(sessionKey: string): string {
  const key = `${CHECKOUT_SESSION_ATTEMPT_PREFIX}${String(sessionKey).trim()}`;
  try {
    const existing = sessionStorage.getItem(key);
    if (existing && /^[a-z0-9]+$/i.test(existing)) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
        : simpleHash(`${sessionKey}:${Date.now()}`);
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return simpleHash(`${sessionKey}:local`);
  }
}

function clearCheckoutAttemptId(cartFingerprintHash: string): void {
  try {
    sessionStorage.removeItem(`${CHECKOUT_ATTEMPT_PREFIX}${cartFingerprintHash}`);
  } catch {
    /* ignore */
  }
}

/**
 * Canonical checkout idempotency:
 * checkout:{organization_id}:{telegram_user_id}:{cart_fingerprint}:{checkout_attempt_id}
 * 167A — attempt id is session-scoped; fingerprint excludes paymentMethod.
 */
function buildCheckoutIdempotencyKey(input: {
  organizationId?: string;
  telegramUserId?: string | null;
  cartFingerprint: string;
  sessionKey: string;
}): string {
  const org = String(input.organizationId ?? "asiastyle").trim() || "asiastyle";
  const uid = String(input.telegramUserId ?? "anon").trim() || "anon";
  const cartFp = simpleHash(input.cartFingerprint);
  const attempt = getOrCreateCheckoutSessionAttemptId(input.sessionKey);
  // Keep legacy per-fingerprint attempt for clear-on-success compatibility.
  void getOrCreateCheckoutAttemptId(cartFp);
  return `checkout:${org}:${uid}:${cartFp}:${attempt}`;
}

function initialCheckoutPaymentMethod(): PaymentMethodChoice {
  if (COMPILE_BANK_IBAN_ONLY) return "bank_transfer_fop";
  const enabled = enabledCustomerCheckoutPaymentMethods();
  if (enabled.length === 1 && enabled[0]) return enabled[0];
  if (
    enabled.includes("novapay_card") &&
    !isNovapayCheckoutDegradedStaging() &&
    !isNovapayNotLiveUi()
  ) {
    return "novapay_card";
  }
  if (enabled.includes("bank_transfer_fop")) return "bank_transfer_fop";
  return enabled[0] ?? "bank_transfer_fop";
}

function bootCheckoutProfile() {
  const book = readAddressBook();
  const resolution = resolveCheckoutDeliveryProfile({
    addresses: book,
    sessionSelectedId: readCheckoutSessionProfileId(),
  });
  return { book, resolution };
}

export function CheckoutV2View({
  lang,
  sessionKey,
  items,
  promoCode,
  onInc,
  onDec,
  onRemove,
  onAddSearchHit,
  recommendations = [],
  onAddRecommendation,
  onOpenRecommendation,
  onRegisterActionable,
  onRecommendationImpression,
  onRecommendationsRefresh,
  canRefreshRecommendations = false,
  recommendationsRefreshing = false,
  recommendationsLoading = false,
  onChangeCart,
  onOpenLegalOffer,
  onOpenLegalPrivacy,
  onOpenBuyerProfiles,
  onSuccess,
  onTrustedPrice,
}: CheckoutV2ViewProps) {
  const totals = computeTotals(items, promoCode);
  const availabilityPolicy = useMemo(() => evaluateCheckoutAvailabilityPolicy(items), [items]);
  const managerConfirmOnly = availabilityPolicy.managerConfirmOnly;
  const boot = useMemo(() => bootCheckoutProfile(), []);
  const [addressBook, setAddressBook] = useState<CustomerSavedAddressV2[]>(boot.book);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    boot.resolution.status === "selected" ? boot.resolution.address.id : null,
  );
  const [showProfilePicker, setShowProfilePicker] = useState(
    boot.resolution.status === "needs_selection",
  );
  const [editorOpen, setEditorOpen] = useState(boot.resolution.status === "needs_editor");
  const [creatingNewAddress, setCreatingNewAddress] = useState(false);
  const [updateSavedOnEdit, setUpdateSavedOnEdit] = useState(false);
  const editorSnapshotRef = useRef<{
    selectedProfileId: string | null;
    name: string;
    surname: string;
    phone: string;
    city: string;
    warehouse: string;
    note: string;
    npSelection: NovaPoshtaReferenceSelection | null;
    npManualFallback: boolean;
  } | null>(null);
  const prefill = useMemo(() => {
    if (boot.resolution.status === "selected") {
      return applyDeliveryProfileToCheckout(boot.resolution.address);
    }
    if (boot.resolution.status === "needs_editor" && boot.resolution.contact) {
      return {
        name: boot.resolution.contact.name,
        surname: boot.resolution.contact.surname,
        phone: boot.resolution.contact.phone,
        city: "",
        warehouse: "",
        note: boot.resolution.contact.comment ?? "",
        np_selection: null as NovaPoshtaReferenceSelection | null,
      };
    }
    return readCheckoutPrefill();
  }, [boot]);
  const [name, setName] = useState(prefill?.name ?? "");
  const [surname, setSurname] = useState(prefill?.surname ?? "");
  const [phone, setPhone] = useState(prefill?.phone ?? "");
  const npDraft = useMemo(() => readNpCheckoutDraft(), []);
  const npRefEnabled = isNovaPoshtaReferenceCheckoutEnabled();
  const [npSelection, setNpSelection] = useState<NovaPoshtaReferenceSelection | null>(
    npDraft?.selection ?? prefill?.np_selection ?? null,
  );
  const [npManualFallback, setNpManualFallback] = useState(npDraft?.manualFallback ?? false);
  const [city, setCity] = useState(prefill?.city ?? prefill?.np_selection?.settlementLabel ?? "");
  const [warehouse, setWarehouse] = useState(
    prefill?.warehouse ?? prefill?.np_selection?.warehouseLabel ?? "",
  );
  const [note, setNote] = useState(("note" in (prefill ?? {}) ? (prefill as { note?: string }).note : "") ?? "");
  const [reservedStg, setReservedStg] = useState<string | null>(null);

  useEffect(() => {
    if (!isActiveReorderSession(peekReorderSession())) return;
    setNote("");
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const stg = await reserveOrderNumber(sessionKey);
      if (!cancelled && stg) setReservedStg(stg);
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionKey]);

  function captureEditorSnapshot() {
    editorSnapshotRef.current = {
      selectedProfileId,
      name,
      surname,
      phone,
      city,
      warehouse,
      note,
      npSelection,
      npManualFallback,
    };
  }

  function restoreEditorSnapshot() {
    const snap = editorSnapshotRef.current;
    if (!snap) return;
    setSelectedProfileId(snap.selectedProfileId);
    setName(snap.name);
    setSurname(snap.surname);
    setPhone(snap.phone);
    setCity(snap.city);
    setWarehouse(snap.warehouse);
    setNote(snap.note);
    setNpSelection(snap.npSelection);
    setNpManualFallback(snap.npManualFallback);
    writeCheckoutSessionProfileId(snap.selectedProfileId);
  }

  function applyProfile(addr: CustomerSavedAddressV2, opts?: { keepEditor?: boolean }) {
    const applied = applyDeliveryProfileToCheckout(addr);
    setSelectedProfileId(addr.id);
    writeCheckoutSessionProfileId(addr.id);
    setName(applied.name);
    setSurname(applied.surname);
    setPhone(applied.phone);
    setCity(applied.city);
    setWarehouse(applied.warehouse);
    setNote(applied.note);
    setNpSelection(applied.np_selection);
    writeNpCheckoutDraft({ selection: applied.np_selection, manualFallback: false });
    setNpManualFallback(false);
    setShowProfilePicker(false);
    setCreatingNewAddress(false);
    setUpdateSavedOnEdit(false);
    if (!opts?.keepEditor) setEditorOpen(false);
  }

  function persistAddressIfRequested() {
    if (!updateSavedOnEdit) return;
    const sel = npSelection;
    const delivery_type = sel
      ? deliveryTypeFromMode(sel.mode)
      : "NOVA_POSHTA_BRANCH";
    const candidate: CustomerSavedAddressV2 = {
      id: creatingNewAddress || !selectedProfileId ? `addr-${Date.now().toString(36)}` : selectedProfileId,
      label: deliveryMethodShortLabel(lang, delivery_type),
      name: name.trim(),
      surname: surname.trim(),
      phone: phone.trim(),
      city: city.trim(),
      warehouse: warehouse.trim(),
        is_default:
          creatingNewAddress || !selectedProfileId
            ? addressBook.length === 0
            : Boolean(addressBook.find((a) => a.id === selectedProfileId)?.is_default),
      updated_at: new Date().toISOString(),
      delivery_type,
      ...(sel?.settlementRef ? { settlement_ref: sel.settlementRef } : {}),
      ...(sel?.warehouseRef && sel.mode !== "address" && sel.mode !== "parcel_locker"
        ? { branch_ref: sel.warehouseRef }
        : {}),
      ...(sel?.warehouseRef && sel.mode === "parcel_locker" ? { locker_ref: sel.warehouseRef } : {}),
      ...(sel?.warehouseNumber ? { branch_number: sel.warehouseNumber } : {}),
      ...(sel?.addressLine ? { street: sel.addressLine } : {}),
      ...(sel?.building ? { building: sel.building } : {}),
      ...(sel?.flat ? { apartment: sel.flat } : {}),
      ...(note.trim() ? { comment: note.trim() } : {}),
      ...(sel ? { np_selection: sel } : {}),
    };
    const dup = findDuplicateSavedAddress(addressBook, candidate);
    const toSave = dup ? { ...candidate, id: dup.id, is_default: dup.is_default || candidate.is_default } : candidate;
    const next = upsertSavedAddress(toSave);
    setAddressBook(next);
    setSelectedProfileId(toSave.id);
    writeCheckoutSessionProfileId(toSave.id);
    if (sel?.settlementRef) {
      writeCheckoutPrefill({
        name: name.trim(),
        surname: surname.trim(),
        phone: phone.trim(),
        city: city.trim(),
        warehouse: warehouse.trim(),
        locale: lang,
        np_selection: sel,
      });
      void persistCustomerRuntimeProfile({
        first_name: name.trim(),
        last_name: surname.trim(),
        phone: phone.trim(),
        city: city.trim(),
        warehouse: warehouse.trim(),
        np_selection: sel,
        durable: true,
      });
    }
  }
  const enabledPaymentMethods = useMemo(() => enabledCustomerCheckoutPaymentMethods(), []);
  const singleBankCheckout = COMPILE_BANK_IBAN_ONLY || isSingleBankIbanCheckout();
  const showPaymentRadios = !COMPILE_BANK_IBAN_ONLY && enabledPaymentMethods.length > 1;
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodChoice>(initialCheckoutPaymentMethod);
  const [paymentTiming, setPaymentTiming] = useState<CustomerPaymentTiming>("immediate");
  const [noteExpanded, setNoteExpanded] = useState(() => Boolean(String(note ?? "").trim()));
  const [touched, setTouched] = useState<TouchedMap>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [prepared, setPrepared] = useState<{
    orderId: string;
    displayNumber?: string;
    paymentUrl: string | null;
    handoffUrl: string;
    totalUah: number;
    contactName: string;
    contactPhone?: string;
  } | null>(null);
  const [preparingHandoff, setPreparingHandoff] = useState(false);
  /** BUYER-INFRA-R1 — null = retail CONSUMER (default). Only APPROVED business profiles selectable. */
  const [buyerProfiles, setBuyerProfiles] = useState<BuyerProfilePublic[]>([]);
  const [retailBuyerLabel, setRetailBuyerLabel] = useState("Особисто · Роздрібний покупець");
  const [selectedBuyerProfileId, setSelectedBuyerProfileId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchBuyerProfilesV2().then((res) => {
      if (cancelled || !res.ok) return;
      setRetailBuyerLabel(res.retail_default.label_uk);
      setBuyerProfiles(res.profiles);
      setSelectedBuyerProfileId((prev) => {
        if (!prev) return null;
        const stillOk = res.profiles.some((p) => p.id === prev && p.selectable_as_verified_buyer);
        return stillOk ? prev : null;
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const checkoutDirty =
    Boolean(name.trim()) ||
    Boolean(surname.trim()) ||
    Boolean(phone.trim()) ||
    Boolean(note.trim()) ||
    Boolean(city.trim()) ||
    Boolean(warehouse.trim()) ||
    Boolean(npSelection?.settlementRef) ||
    npManualFallback;
  useRegisterGlobalAppCloseDirty(
    "checkout_v2",
    checkoutDirty,
    note.trim() ? "checkout_comment" : npSelection ? "np_selection" : "checkout_form",
  );
  const fingerprintRef = useRef("");
  /** COMMERCE-LAUNCH-168D1 — flush isolated field drafts before validate/submit. */
  const fieldFlushersRef = useRef<Map<string, () => string>>(new Map());
  const registerFieldFlush = useCallback((id: string, flush: () => string) => {
    fieldFlushersRef.current.set(id, flush);
  }, []);
  const flushIsolatedFields = useCallback((): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const [id, flush] of fieldFlushersRef.current.entries()) {
      out[id] = flush();
    }
    return out;
  }, []);

  useEffect(() => {
    if (enabledPaymentMethods.length === 1 && enabledPaymentMethods[0] && paymentMethod !== enabledPaymentMethods[0]) {
      setPaymentMethod(enabledPaymentMethods[0]);
    }
  }, [enabledPaymentMethods, paymentMethod]);

  const validation = validateCheckout({
    name,
    surname,
    phone,
    city,
    warehouse,
    note,
    paymentMethod,
  });
  const readiness = evaluateCheckoutReadiness({
    name,
    surname,
    phone,
    city,
    warehouse,
    note,
    paymentMethod,
    itemCount: items.length,
    submitting,
    preparingHandoff,
    delivery:
      npRefEnabled && !npManualFallback
        ? {
            npRefEnabled: true,
            npManualFallback: false,
            settlementRef: npSelection?.settlementRef ?? null,
            warehouseRef: npSelection?.warehouseRef ?? null,
            mode: npSelection?.mode ?? null,
            city,
            warehouse,
          }
        : { npRefEnabled: false, city, warehouse },
  });
  const show = (f: keyof CheckoutFieldErrors) => (touched[f] ? validation.errors[f] : undefined);
  const markTouched = (f: keyof CheckoutFieldErrors) => setTouched((t) => ({ ...t, [f]: true }));

  const buildCheckoutExtras = () => ({
    novaPoshtaDelivery:
      npRefEnabled && !npManualFallback && npSelection
        ? selectionToCheckoutPayload(npSelection)
        : null,
    novaPoshtaManualFallback: npRefEnabled && npManualFallback,
  });

  const ensureDeliveryReady = async (): Promise<boolean> => {
    if (npRefEnabled && !npManualFallback) {
      if (!npSelection) {
        setError(
          lang === "en"
            ? "Select settlement and branch from Nova Poshta search."
            : "Оберіть населений пункт і відділення з пошуку Nova Poshta.",
        );
        setTouched((t) => ({ ...t, city: true, warehouse: true }));
        return false;
      }
      if (npSelection.mode !== "address" && !npSelection.warehouseRef) {
        setError(lang === "en" ? "Select a branch or parcel locker." : "Оберіть відділення або поштомат.");
        return false;
      }
      if (!isCheckoutDeliverySelectionConsistent(npSelection)) {
        setError(
          lang === "en"
            ? "Select city and branch from the Nova Poshta list."
            : "Оберіть місто і відділення зі списку Nova Poshta.",
        );
        return false;
      }
      if (npSelection.mode === "address" && !npSelection.building?.trim()) {
        setError(lang === "en" ? "Enter building for address delivery." : "Вкажіть будинок для адресної доставки.");
        return false;
      }
      // R8-R2 — confirmed local snapshot with provider refs: do NOT re-call Nova Poshta
      // merely to create the payment / order session.
      const hasConfirmedSnapshot =
        npSelection.mode === "address"
          ? Boolean(npSelection.settlementRef)
          : Boolean(npSelection.settlementRef && npSelection.warehouseRef && npSelection.warehouseRef.length >= 8);
      if (npSelection.mode !== "address" && !hasConfirmedSnapshot) {
        const live = await validateNovaPoshtaSelection(npSelection);
        if (!live.ok) {
          setError(
            live.errors[0]?.message ??
              (lang === "en"
                ? "Nova Poshta temporarily unavailable. Retry or enter the branch manually."
                : "Нова Пошта тимчасово не відповідає. Спробуйте ще раз або введіть відділення вручну."),
          );
          setRetryable(true);
          return false;
        }
      }
    }
    return true;
  };

  const amountLabel = formatUah(Math.ceil(totals.totalUah));
  const totalDigits = amountLabel.replace(/\s*₴\s*$/u, "").trim();
  const ctaLabel =
    submitting
      ? t(lang, "creating_order")
      : paymentMethod === "novapay_card"
      ? preparingHandoff
        ? lang === "en"
          ? "Preparing secure payment…"
          : "Готуємо захищену оплату…"
        : lang === "en"
          ? `Confirm and pay ${amountLabel}`
          : `Підтвердити й оплатити ${amountLabel}`
      : paymentMethod === "nova_poshta_cod"
        ? lang === "en"
          ? "Confirm order"
          : "Підтвердити замовлення"
      : paymentTiming === "after_manager_confirmation"
        ? lang === "en"
          ? `Create order · ${totalDigits} ₴`
          : `Створити замовлення · ${totalDigits} ₴`
      : isActiveReorderSession(peekReorderSession())
        ? t(lang, "bank_create_reorder", { TOTAL: totalDigits })
        : t(lang, "bank_create_and_pay", { TOTAL: totalDigits });

  /**
   * COMMERCE-LAUNCH-167A-R1 — NO warm placeOrder.
   * Delivery/payment edits must NOT create orders, emit order_created, or Telegram.
   * Only explicit CTA (`submit`) may call submitCheckoutV2 → placeOrder.
   * (Removed NOVAPAY-116-R5A auto warm-handoff useEffect.)
   *
   * BANK-IBAN-R3.6 — cart qty edits use the existing cart SoT and happen BEFORE STG.
   * ORDER_AMENDMENT: after instruction exists, do not silently mutate amount.
   * Payment purpose is generated server-side only after the public STG number exists.
   */

  const finishSuccess = (
    orderId: string,
    status: string | undefined,
    displayNumber: string | undefined,
    method: PaymentMethodChoice,
    meta: {
      totalUah?: number;
      contactName?: string;
      contactPhone?: string;
      bankPayment?: BankPaymentClientPayload | null;
      bankSheetOpen?: boolean;
      paymentTiming?: CustomerPaymentTiming;
      autoBankHandoff?: boolean;
    },
  ) => {
    try {
      const fp = fingerprintRef.current;
      if (fp) clearCheckoutAttemptId(simpleHash(fp));
    } catch {
      /* ignore */
    }
    onSuccess(orderId, status, displayNumber, method, meta);
    const publicStg = String(displayNumber ?? "").trim();
    const sel = npSelection;
    writeCheckoutPrefill({
      name: name.trim(),
      surname: surname.trim(),
      phone: phone.trim(),
      city: city.trim() || sel?.settlementLabel || "",
      warehouse: warehouse.trim() || sel?.warehouseLabel || "",
      note: note.trim(),
      locale: lang,
      ...(sel ? { np_selection: sel } : {}),
    });
    clearNpCheckoutDraft();
    void persistCustomerRuntimeProfile({
      first_name: name.trim(),
      last_name: surname.trim(),
      phone: phone.trim(),
      city: city.trim() || sel?.settlementLabel || "",
      warehouse: warehouse.trim() || sel?.warehouseLabel || "",
      np_selection: sel,
      source_order_id: publicStg || null,
      durable: true,
    });
  };

  const submit = async (timingArg?: CustomerPaymentTiming) => {
    if (submitting) return;
    const timing = timingArg ?? paymentTiming;
    setPaymentTiming(timing);
    // Commit IME drafts before validation (blur may not have fired yet).
    // Use returned values — React setState from flush is async.
    const flushed = flushIsolatedFields();
    const nameNow = flushed["sv2-name"] ?? name;
    const surnameNow = flushed["sv2-surname"] ?? surname;
    const phoneNow = flushed["sv2-phone"] ?? phone;
    const cityNow = flushed["sv2-city"] ?? city;
    const warehouseNow = flushed["sv2-wh"] ?? warehouse;
    const noteNow = flushed["sv2-note"] ?? note;
    if (!(await ensureDeliveryReady())) return;
    const cityForValidation =
      npRefEnabled && !npManualFallback && npSelection
        ? npSelection.settlementLabel
        : cityNow;
    const whForValidation =
      npRefEnabled && !npManualFallback && npSelection
        ? npSelection.warehouseLabel ?? npSelection.settlementLabel
        : warehouseNow;
    const v = validateCheckout({
      name: nameNow,
      surname: surnameNow,
      phone: phoneNow,
      city: cityForValidation,
      warehouse: whForValidation,
      note: noteNow,
      paymentMethod,
    });
    if (!v.ok || !paymentMethod) {
      setTouched({
        name: true,
        surname: true,
        phone: true,
        city: true,
        warehouse: true,
        note: true,
        paymentMethod: true,
      });
      setError(lang === "en" ? "Please check the highlighted fields." : "Перевірте, будь ласка, виділені поля.");
      return;
    }

    if (paymentMethod === "bank_transfer_fop") {
      emitEvent("bank_checkout_cta_clicked", { amount_digits: totalDigits, payment_timing: timing });
      emitEvent("order_create_started", { payment_timing: timing });
    }

    // R5A/167A — only reuse prepared order when it was created by an earlier explicit submit
    // in this same click session (warm auto-place removed).
    if (paymentMethod === "novapay_card" && prepared?.orderId) {
      const opened = openNovapayHandoffSync({
        orderId: prepared.orderId,
        paymentUrl: prepared.paymentUrl,
        handoffUrl: prepared.handoffUrl,
      });
      finishSuccess(prepared.orderId, "placed", prepared.displayNumber, "novapay_card", {
        totalUah: prepared.totalUah,
        contactName: prepared.contactName,
        ...(prepared.contactPhone ? { contactPhone: prepared.contactPhone } : {}),
      });
      if (opened.kind !== "opened") {
        setError(
          lang === "en"
            ? "Could not open payment automatically. Use «Continue to payment» on the next screen."
            : "Не вдалося автоматично відкрити оплату. На наступному екрані натисніть «Перейти до оплати».",
        );
      }
      return;
    }

    setSubmitting(true);
    setError(null);
    setRetryable(false);
    if (paymentMethod === "novapay_card") setPreparingHandoff(true);
    const sel = npSelection ?? prefill?.np_selection;
    writeCheckoutPrefill({
      name: nameNow.trim(),
      surname: surnameNow.trim(),
      phone: v.normalizedPhone ?? phoneNow.trim(),
      city: v.city,
      warehouse: v.warehouse,
      note: noteNow.trim(),
      locale: lang,
      ...(sel ? { np_selection: sel } : {}),
    });
    void persistCustomerRuntimeProfile({
      first_name: nameNow.trim(),
      last_name: surnameNow.trim(),
      phone: v.normalizedPhone ?? phoneNow.trim(),
      durable: false,
    });
    // COMMERCE-176A — checkout never silently rewrites saved profiles.
    if (updateSavedOnEdit && selectedProfileId) {
      const existing = addressBook.find((a) => a.id === selectedProfileId);
      if (existing) {
        const delivery_type = sel
          ? deliveryTypeFromMode(sel.mode)
          : (existing.delivery_type ?? "NOVA_POSHTA_BRANCH");
        const {
          comment: _ignoredComment,
          np_selection: _ignoredNp,
          updated_at: _ignoredUpdated,
          ...base
        } = existing;
        const patch: Omit<CustomerSavedAddressV2, "updated_at"> & { updated_at?: string } = {
          ...base,
          name: nameNow.trim(),
          surname: surnameNow.trim(),
          phone: v.normalizedPhone ?? phoneNow.trim(),
          city: v.city,
          warehouse: v.warehouse,
          delivery_type,
          is_default: existing.is_default,
        };
        if (noteNow.trim()) patch.comment = noteNow.trim();
        else if (existing.comment) patch.comment = existing.comment;
        if (sel) patch.np_selection = sel;
        else if (existing.np_selection) patch.np_selection = existing.np_selection;
        upsertSavedAddress(patch);
      }
    }
    writeNpCheckoutDraft({
      selection: npSelection,
      manualFallback: npManualFallback,
    });
    const checkoutFp = buildPlaceOrderIdempotencyFingerprint({
      sessionKey,
      contactName: v.contactName,
      normalizedPhone: v.normalizedPhone ?? null,
      city: v.city,
      warehouse: v.warehouse,
      items,
      promoCode: totals.promoStatus === "applied" ? (totals.promoCode ?? "") : "",
    });
    fingerprintRef.current = checkoutFp;
    const result = await submitCheckoutV2({
      sessionKey,
      items,
      promoCode: totals.promoStatus === "applied" ? totals.promoCode : null,
      contactName: v.contactName,
      contactPhone: v.normalizedPhone ?? phoneNow.trim(),
      deliveryCity: v.city || undefined,
      deliveryWarehouse: v.warehouse || undefined,
      note: v.sanitizedNote || undefined,
      paymentMethod,
      paymentTiming:
        managerConfirmOnly
          ? "after_manager_confirmation"
          : singleBankCheckout || paymentMethod === "bank_transfer_fop"
            ? timing
            : "immediate",
      availabilityReviewRequired: managerConfirmOnly,
      availabilityLineReasons: managerConfirmOnly
        ? formatAvailabilityLineReasons(availabilityPolicy.lines)
        : null,
      reorderFromPublicOrderNumber: peekReorderSession()?.sourcePublicOrderNumber ?? null,
      buyerProfileId: selectedBuyerProfileId,
      idempotencyKey: buildCheckoutIdempotencyKey({
        cartFingerprint: checkoutFp,
        telegramUserId: telegramUserId(),
        sessionKey,
      }),
      ...buildCheckoutExtras(),
    });
    if (result.ok && result.orderId && paymentMethod === "novapay_card") {
      const handoff = await prepareNovapayHandoff({
        orderId: result.orderId,
        sessionKey,
        idempotencyKey: `handoff:${result.orderId}:novapay`,
      });
      const handoffUrl = handoff.handoff_url || buildHandoffGoUrl(result.orderId, sessionKey);
      setPrepared({
        orderId: result.orderId,
        ...(result.displayOrderNumber ? { displayNumber: result.displayOrderNumber } : {}),
        paymentUrl: handoff.payment_url ?? null,
        handoffUrl,
        totalUah: result.totalUah ?? totals.totalUah,
        contactName: v.contactName,
        ...(v.normalizedPhone ? { contactPhone: v.normalizedPhone } : {}),
      });
      const opened = openNovapayHandoffSync({
        orderId: result.orderId,
        paymentUrl: handoff.payment_url ?? null,
        handoffUrl,
      });
      setPreparingHandoff(false);
      setSubmitting(false);
      finishSuccess(result.orderId, result.status, result.displayOrderNumber, paymentMethod, {
        totalUah: result.totalUah ?? totals.totalUah,
        contactName: v.contactName,
        ...(v.normalizedPhone ? { contactPhone: v.normalizedPhone } : {}),
      });
      emitEvent("payment_novapay_create_requested", {
        order_id: result.orderId,
        warm_handoff: false,
        explicit_submit: true,
        ok: handoff.ok === true,
      });
      if (opened.kind !== "opened") {
        setError(
          lang === "en"
            ? "Could not open payment automatically. Use «Continue to payment» on the next screen."
            : "Не вдалося автоматично відкрити оплату. На наступному екрані натисніть «Перейти до оплати».",
        );
      }
      return;
    }
    setPreparingHandoff(false);
    setSubmitting(false);
    if (result.ok && result.orderId && paymentMethod === "bank_transfer_fop") {
      const bankPayment = result.bankPayment ?? null;
      if (bankPayment) {
        emitEvent("bank_instruction_created", {
          public_order_number: bankPayment.public_order_number,
        });
      }
      emitEvent("order_create_succeeded", {
        payment_timing: timing,
        public_order_number: result.displayOrderNumber ?? bankPayment?.public_order_number ?? null,
      });
      if (timing === "immediate" && bankPayment) {
        const uri = bankPayment.launch?.uri ?? null;
        emitEvent("bank_app_open_attempted", {
          public_order_number: bankPayment.public_order_number,
          has_uri: Boolean(uri),
        });
        const opened = Boolean(uri && tryOpenNbuBankApp(uri));
        emitEvent(opened ? "bank_app_open_succeeded" : "bank_app_open_fallback", {
          public_order_number: bankPayment.public_order_number,
        });
      }
      const session = peekReorderSession();
      if (session && isActiveReorderSession(session)) {
        const created = String(result.displayOrderNumber ?? "").trim();
        patchReorderSession({ newPublicOrderNumber: created || null });
        emitEvent("reorder_order_created", {
          source_public_order_number: session.sourcePublicOrderNumber,
          new_public_order_number: created || null,
          source_order_status: session.sourceOrderStatus,
          source_payment_status: session.sourcePaymentStatus,
          item_count_source: session.itemCountSource,
          item_count_added: session.itemCountAdded,
          item_count_unavailable: session.itemCountUnavailable,
          cart_value_current: session.cartValueCurrent,
        });
      }
      finishSuccess(result.orderId, result.status, result.displayOrderNumber, paymentMethod, {
        totalUah: result.totalUah ?? totals.totalUah,
        contactName: v.contactName,
        ...(v.normalizedPhone ? { contactPhone: v.normalizedPhone } : {}),
        bankPayment,
        bankSheetOpen: false,
        paymentTiming: result.paymentTiming ?? timing,
        autoBankHandoff: timing === "immediate" && Boolean(bankPayment),
      });
      return;
    }
    if (result.ok && result.orderId) {
      finishSuccess(result.orderId, result.status, result.displayOrderNumber, paymentMethod, {
        totalUah: result.totalUah ?? totals.totalUah,
        contactName: v.contactName,
        ...(v.normalizedPhone ? { contactPhone: v.normalizedPhone } : {}),
      });
      return;
    }
    if (
      result.error === "price_changed" &&
      result.productId &&
      typeof result.trustedUnitPriceUah === "number" &&
      result.trustedUnitPriceUah > 0
    ) {
      onTrustedPrice?.(result.productId, result.trustedUnitPriceUah);
      setError(
        lang === "en"
          ? (result.messageEn ?? t(lang, "price_changed_hint"))
          : (result.message ?? t(lang, "price_changed_hint")),
      );
      setRetryable(true);
      setPreparingHandoff(false);
      return;
    }
    const revalidateFail =
      result.error === "catalog_revalidation_unavailable" ||
      result.error === "catalog_snapshot_unavailable";
    if (revalidateFail) {
      setError(
        lang === "en"
          ? (result.messageEn ?? t(lang, "checkout_revalidate_fail"))
          : (result.message ?? t(lang, "checkout_revalidate_fail")),
      );
      setRetryable(true);
      setPreparingHandoff(false);
      return;
    }
    setError(
      lang === "en"
        ? (result.messageEn ?? result.message ?? "Could not place the order. Try again later.")
        : (result.message ?? "Не вдалося оформити замовлення. Спробуйте пізніше."),
    );
    emitEvent("order_create_failed", { reason: result.error ?? "place_order_failed" });
    setPreparingHandoff(false);
  };

  const compactReady = isCheckoutContactDeliveryComplete({
    name,
    surname,
    phone,
    city,
    warehouse,
    npSelection,
    npRefEnabled,
    npManualFallback,
  });
  const showCompactSummary = compactReady && !editorOpen && !showProfilePicker;
  const selectedAddr = addressBook.find((a) => a.id === selectedProfileId) ?? null;
  const summaryDeliveryType = npSelection
    ? deliveryTypeFromMode(npSelection.mode)
    : selectedAddr?.delivery_type;

  const renderContactDeliveryEditor = () => (
    <div className="sv2-checkout-profile-editor" data-testid="sv2-express-delivery" data-form-mode="continuous">
      <h4>{lang === "en" ? "Contact / recipient" : "Контакт / одержувач"}</h4>
      <IsolatedFormField
        id="sv2-name"
        label={`${t(lang, "first_name")} *`}
        value={name}
        onCommit={setName}
        commitFilter={filterPersonNameInput}
        onBlurExtra={() => markTouched("name")}
        error={show("name")}
        autoComplete="given-name"
        maxLength={40}
        registerFlush={registerFieldFlush}
        testId="sv2-name-input"
      />
      <IsolatedFormField
        id="sv2-surname"
        label={`${t(lang, "last_name")} *`}
        value={surname}
        onCommit={setSurname}
        commitFilter={filterPersonNameInput}
        onBlurExtra={() => markTouched("surname")}
        error={show("surname")}
        autoComplete="family-name"
        maxLength={40}
        registerFlush={registerFieldFlush}
        testId="sv2-surname-input"
      />
      <IsolatedFormField
        id="sv2-phone"
        label={`${t(lang, "phone")} *`}
        value={phone}
        onCommit={setPhone}
        commitFilter={filterPhoneInput}
        onBlurExtra={() => markTouched("phone")}
        error={show("phone")}
        hint={!show("phone") ? phoneLiveHint(phone) : null}
        inputMode="tel"
        placeholder="+380XXXXXXXXX"
        autoComplete="tel"
        registerFlush={registerFieldFlush}
        testId="sv2-phone-input"
      />
      <h4 data-testid="sv2-np-delivery-heading">
        {lang === "en" ? "🚚 Nova Poshta delivery" : "🚚 Доставка Новою поштою"}
      </h4>
      {npRefEnabled ? (
        <>
          <NovaPoshtaDeliverySection
            key={`${selectedProfileId ?? "new"}:${creatingNewAddress ? "create" : "edit"}`}
            lang={lang}
            initialSelection={npSelection}
            manualFallback={npManualFallback}
            onManualFallbackChange={(manual) => {
              setNpManualFallback(manual);
              writeNpCheckoutDraft({ selection: manual ? null : npSelection, manualFallback: manual });
            }}
            onSelectionChange={(sel) => {
              setNpSelection(sel);
              writeNpCheckoutDraft({ selection: sel, manualFallback: npManualFallback });
            }}
            onDisplayChange={(c, w) => {
              setCity(c);
              setWarehouse(w);
            }}
          />
          {npManualFallback ? (
            <>
              <IsolatedFormField
                id="sv2-city"
                label={`${t(lang, "city_np")} *`}
                value={city}
                onCommit={setCity}
                commitFilter={(raw) => raw.slice(0, 120)}
                onBlurExtra={() => markTouched("city")}
                error={show("city")}
                registerFlush={registerFieldFlush}
                testId="sv2-city-input"
              />
              <IsolatedFormField
                id="sv2-wh"
                label={`${t(lang, "warehouse")} *`}
                value={warehouse}
                onCommit={setWarehouse}
                commitFilter={(raw) => raw.slice(0, 120)}
                onBlurExtra={() => markTouched("warehouse")}
                error={show("warehouse")}
                registerFlush={registerFieldFlush}
                testId="sv2-wh-input"
              />
            </>
          ) : null}
          {!npManualFallback && show("city") ? (
            <div className="sv2-field-err" data-testid="sv2-np-city-err">
              {show("city")}
            </div>
          ) : null}
          {!npManualFallback && show("warehouse") ? (
            <div className="sv2-field-err" data-testid="sv2-np-warehouse-err">
              {show("warehouse")}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <IsolatedFormField
            id="sv2-city"
            label={`${t(lang, "city_np")} *`}
            value={city}
            onCommit={setCity}
            commitFilter={(raw) => raw.slice(0, 120)}
            onBlurExtra={() => markTouched("city")}
            error={show("city")}
            registerFlush={registerFieldFlush}
            testId="sv2-city-input"
          />
          <IsolatedFormField
            id="sv2-wh"
            label={`${t(lang, "warehouse")} *`}
            value={warehouse}
            onCommit={setWarehouse}
            commitFilter={(raw) => raw.slice(0, 120)}
            onBlurExtra={() => markTouched("warehouse")}
            error={show("warehouse")}
            registerFlush={registerFieldFlush}
            testId="sv2-wh-input"
          />
        </>
      )}
      <label className="sv2-address-default" data-testid="sv2-checkout-update-saved">
        <input
          type="checkbox"
          checked={updateSavedOnEdit}
          onChange={(e) => setUpdateSavedOnEdit(e.target.checked)}
        />
        {t(lang, "checkout_save_to_cabinet")}
      </label>
      <div className="sv2-checkout-editor-actions">
        <SecondaryCommerceAction
          testId="sv2-checkout-editor-cancel"
          onClick={() => {
            const snap = editorSnapshotRef.current;
            restoreEditorSnapshot();
            const complete =
              snap != null &&
              isCheckoutContactDeliveryComplete({
                name: snap.name,
                surname: snap.surname,
                phone: snap.phone,
                city: snap.city,
                warehouse: snap.warehouse,
                npSelection: snap.npSelection,
                npRefEnabled,
                npManualFallback: snap.npManualFallback,
              });
            setEditorOpen(!complete);
            setShowProfilePicker(false);
            setCreatingNewAddress(false);
          }}
        >
          {t(lang, "cancel")}
        </SecondaryCommerceAction>
        <PrimaryCommerceAction
          testId="sv2-checkout-editor-save"
          onClick={() => {
            persistAddressIfRequested();
            setEditorOpen(false);
            setShowProfilePicker(false);
            setCreatingNewAddress(false);
          }}
        >
          {t(lang, "save")}
        </PrimaryCommerceAction>
      </div>
    </div>
  );

  return (
    <CommercePageShell>
    <div className="sv2-body" data-testid="sv2-express-checkout">
      <div className="sv2-section-title">{t(lang, "checkout")}</div>
      {reservedStg ? (
        <div className="sv2-info-card" data-testid="sv2-checkout-reserved-stg" style={{ marginBottom: 12 }}>
          <strong>{lang === "en" ? "Order number" : "Номер замовлення"}</strong>
          <div className="sv2-order-id" style={{ marginTop: 4 }}>
            {reservedStg}
          </div>
          {/* CUSTOMER-R4.15 — PAYMENT_PURPOSE_REQUISITES: purpose only on bank details after create, not STG header. */}
        </div>
      ) : null}

      <section className="sv2-express-card" data-testid="sv2-checkout-buyer">
        <h3>{lang === "en" ? "Buyer" : "Покупець"}</h3>
        <p className="sv2-muted" style={{ fontSize: 12, marginTop: 0, marginBottom: 10 }}>
          {lang === "en"
            ? "Who pays / needs documents. Delivery recipient below stays separate."
            : "Хто оплачує / потребує документів. Одержувач доставки нижче — окремо."}
        </p>
        <label
          className={`sv2-delivery-profile-card sv2-checkout-profile-option${
            selectedBuyerProfileId == null ? " is-selected" : ""
          }`}
          data-testid="sv2-checkout-buyer-retail"
          style={{ display: "block", cursor: "pointer", marginBottom: 8 }}
        >
          <input
            type="radio"
            name="sv2-buyer"
            checked={selectedBuyerProfileId == null}
            onChange={() => setSelectedBuyerProfileId(null)}
            style={{ marginRight: 8 }}
          />
          <strong>{lang === "en" ? "Personally · Retail buyer" : retailBuyerLabel}</strong>
          <div className="sv2-muted" style={{ fontSize: 12, marginTop: 4, marginLeft: 22 }}>
            {lang === "en" ? "Default — no tax ID required." : "За замовчуванням — без ЄДРПОУ / РНОКПП."}
          </div>
        </label>
        {buyerProfiles.map((p) => {
          const selectable = p.selectable_as_verified_buyer;
          return (
            <label
              key={p.id}
              className={`sv2-delivery-profile-card sv2-checkout-profile-option${
                selectedBuyerProfileId === p.id ? " is-selected" : ""
              }${selectable ? "" : " is-disabled"}`}
              data-testid="sv2-checkout-buyer-business"
              data-verification-status={p.verification_status}
              style={{
                display: "block",
                cursor: selectable ? "pointer" : "not-allowed",
                marginBottom: 8,
                opacity: selectable ? 1 : 0.65,
              }}
            >
              <input
                type="radio"
                name="sv2-buyer"
                disabled={!selectable}
                checked={selectedBuyerProfileId === p.id}
                onChange={() => {
                  if (selectable) setSelectedBuyerProfileId(p.id);
                }}
                style={{ marginRight: 8 }}
              />
              <strong>
                {buyerTypeLabelUk(p.type)} · {p.legal_name || p.display_name}
              </strong>
              <div className="sv2-muted" style={{ fontSize: 12, marginTop: 4, marginLeft: 22 }}>
                {!selectable
                  ? p.type === "FOP"
                    ? lang === "en"
                      ? "FOP details are being verified · not selectable until approved"
                      : "Реквізити ФОП перевіряються. Поки перевірка не завершена, ви можете оформити замовлення як фізична особа."
                    : lang === "en"
                      ? "Company not approved for orders yet · not selectable"
                      : "Компанія ще не схвалена для оформлення замовлень."
                  : p.verification_status_uk}
              </div>
            </label>
          );
        })}
        {onOpenBuyerProfiles ? (
          <SecondaryCommerceAction testId="sv2-checkout-add-buyer-profile" onClick={onOpenBuyerProfiles}>
            {lang === "en" ? "Add business profile" : "Додати бізнес-профіль"}
          </SecondaryCommerceAction>
        ) : null}
      </section>

      <section
        className="sv2-express-card"
        data-testid="sv2-express-contact-delivery"
        data-form-mode="continuous"
        data-np-ref-enabled={npRefEnabled ? "true" : "false"}
      >
        <h3>{t(lang, "checkout_contact_delivery")}</h3>
        {showCompactSummary ? (
          <div className="sv2-contact-delivery-summary" data-testid="sv2-checkout-delivery-profile">
            <div data-testid="sv2-checkout-selected-profile">
              <strong>{[name, surname].filter(Boolean).join(" ")}</strong>
              <div className="sv2-muted" style={{ fontSize: 13 }}>
                {formatUaPhoneDisplay(phone)}
              </div>
              <div style={{ fontSize: 13, marginTop: 8 }}>
                {lang === "en" ? "Nova Poshta" : "Нова пошта"} · {deliveryMethodShortLabel(lang, summaryDeliveryType)}
              </div>
              <div style={{ fontSize: 13 }}>{city || selectedAddr?.city}</div>
              <div style={{ fontSize: 13 }}>
                {warehouse || (selectedAddr ? formatDeliveryDestinationLine(selectedAddr) : "")}
              </div>
            </div>
            <div className="sv2-contact-delivery-actions">
              <SecondaryCommerceAction
                testId="sv2-checkout-change-profile"
                onClick={() => {
                  captureEditorSnapshot();
                  const readyCount = addressBook.filter((a) => !a.archived).length;
                  if (readyCount > 1) {
                    setShowProfilePicker(true);
                    setEditorOpen(false);
                  } else {
                    setShowProfilePicker(false);
                    setEditorOpen(true);
                  }
                }}
              >
                {lang === "en" ? "Change" : "Змінити"}
              </SecondaryCommerceAction>
            </div>
          </div>
        ) : null}
        {showProfilePicker ? (
          <div className="sv2-checkout-profile-picker" data-testid="sv2-checkout-profile-picker">
            {addressBook.map((addr) => (
              <button
                key={addr.id}
                type="button"
                className={`sv2-delivery-profile-card sv2-checkout-profile-option${
                  addr.id === selectedProfileId ? " is-selected" : ""
                }`}
                data-testid={`sv2-checkout-pick-${addr.id}`}
                onClick={() => applyProfile(addr)}
              >
                <strong>{deliveryMethodTitle(lang, addr.delivery_type)}</strong>
                <div className="sv2-muted" style={{ fontSize: 13 }}>
                  {[addr.name, addr.surname].filter(Boolean).join(" ")} · {formatUaPhoneDisplay(addr.phone)}
                </div>
                <div style={{ fontSize: 13 }}>{formatDeliveryDestinationLine(addr)}</div>
              </button>
            ))}
          </div>
        ) : null}
        {!showCompactSummary && !showProfilePicker ? renderContactDeliveryEditor() : null}
        {showCompactSummary && editorOpen ? renderContactDeliveryEditor() : null}
      </section>

      <section className="sv2-express-card" data-testid="sv2-express-items">
        <h3>{lang === "en" ? "Order" : "Замовлення"}</h3>
        <div className="sv2-checkout-items" role="list">
          {items.map((item) => {
            const lineTotal = item.priceUah * item.qty;
            return (
              <article className="sv2-checkout-item" role="listitem" key={item.id} data-testid="sv2-checkout-item">
                <div className="sv2-checkout-item-media">
                  {item.imageUrl ? (
                    <img
                      className="sv2-checkout-item-img"
                      src={miniImageUrl(item.imageUrl) ?? item.imageUrl}
                      alt={item.title}
                      width={64}
                      height={64}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="sv2-checkout-item-img is-placeholder" aria-hidden="true" />
                  )}
                </div>
                <div className="sv2-checkout-item-body">
                  <div className="sv2-checkout-item-title">{item.title}</div>
                  <div className="sv2-checkout-item-meta">
                    {item.sku ? (
                      <span>
                        {t(lang, "store_sku")}: {item.sku}
                      </span>
                    ) : null}
                    {item.brand ? <span>{item.brand}</span> : null}
                    {item.weight ? <span>{item.weight}</span> : null}
                    {item.availabilityLabel ? (
                      <span className="sv2-checkout-item-avail">{item.availabilityLabel}</span>
                    ) : null}
                  </div>
                  <div className="sv2-checkout-item-pricing">
                    <span>
                      {formatUah(item.priceUah)} × {item.qty}
                    </span>
                    <strong>{formatUah(lineTotal)}</strong>
                  </div>
                  {onInc && onDec && onRemove ? (
                    <div className="sv2-qty-stepper" data-testid={`sv2-checkout-qty-${item.id}`}>
                      <button
                        type="button"
                        className="sv2-qty-btn"
                        data-testid={`sv2-checkout-qty-dec-${item.id}`}
                        aria-label={lang === "en" ? "Decrease quantity" : "Зменшити кількість"}
                        onClick={() => onDec(item.id)}
                      >
                        −
                      </button>
                      <span className="sv2-qty-val" aria-live="polite">
                        {item.qty}
                      </span>
                      <button
                        type="button"
                        className="sv2-qty-btn"
                        data-testid={`sv2-checkout-qty-inc-${item.id}`}
                        aria-label={lang === "en" ? "Increase quantity" : "Збільшити кількість"}
                        onClick={() => onInc(item.id)}
                      >
                        ＋
                      </button>
                      <button
                        type="button"
                        className="sv2-del"
                        data-testid={`sv2-checkout-qty-remove-${item.id}`}
                        onClick={() => onRemove(item.id)}
                        style={{ marginLeft: "auto" }}
                      >
                        {t(lang, "delete")}
                      </button>
                    </div>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
        {onAddRecommendation && (recommendations.length > 0 || recommendationsLoading) ? (
          <CanonicalThreeProductRecommendations
            lang={lang}
            items={recommendations.slice(0, 3)}
            onAdd={onAddRecommendation}
            onOpen={(product) => {
              emitEvent("checkout_recommendation_click", { product_id: product.id, source: "checkout" });
              emitEvent("recommendation_click", {
                placement: "CHECKOUT_CROSS_SELL",
                recommended_product_id: product.id,
              });
              onOpenRecommendation?.(product);
            }}
            {...(onRegisterActionable ? { onRegisterActionable } : {})}
            title={t(lang, "checkout_add_order")}
            testId="sv2-checkout-crosssell"
            placement="CHECKOUT_CROSS_SELL"
            onRefresh={() => onRecommendationsRefresh?.()}
            canRefresh={canRefreshRecommendations}
            refreshing={recommendationsRefreshing}
            loading={recommendationsLoading}
            onImpression={(ids) => {
              emitEvent("checkout_recommendation_impression", { product_ids: ids, source: "checkout" });
              onRecommendationImpression?.(ids);
            }}
          />
        ) : null}
        {onAddSearchHit ? (
          <div className="sv2-checkout-cart-edit" data-testid="sv2-checkout-cart-edit">
            <CartProductFinderV2
              lang={lang}
              items={items}
              compact
              openLabel={t(lang, "checkout_add_other")}
              onAdd={onAddSearchHit}
            />
          </div>
        ) : null}
        <div className="sv2-summary" style={{ marginTop: 10 }}>
          {totals.discountUah > 0 ? (
            <div className="sv2-row">
              <span>{t(lang, "discount")}</span>
              <span>−{formatUah(totals.discountUah)}</span>
            </div>
          ) : null}
          <div className="sv2-row sv2-total">
            <span>{t(lang, "amount_due")}</span>
            <span>{amountLabel}</span>
          </div>
        </div>
      </section>

      <section
        className="sv2-express-card"
        data-testid="sv2-express-payment"
        data-payment-mode={singleBankCheckout ? "BANK_IBAN_ONLY" : "MULTI"}
      >
        <h3>{singleBankCheckout || !showPaymentRadios ? t(lang, "payment_section") : t(lang, "choose_payment")}</h3>
        {singleBankCheckout ? (
          <div
            className="sv2-checkout-bank-block"
            data-testid="sv2-checkout-fixed-bank"
            data-payment-method="bank_transfer_fop"
            data-payment-rail="BANK_IBAN_DIRECT_TRANSFER"
            data-iban={STORE_INFO.iban}
            style={{ marginTop: 4 }}
          >
            <strong data-testid="sv2-checkout-bank-method-title">{t(lang, "bank_method_title")}</strong>
            <p className="sv2-muted" style={{ margin: "6px 0 0" }}>
              {t(lang, "bank_fixed_method_direct")}
            </p>
            <span className="sv2-sr-only" data-testid="sv2-checkout-active-iban">
              {STORE_INFO.iban}
            </span>
            <BankPaymentDetailsV2
              lang={lang}
              testId="sv2-checkout-bank-details"
              fields={merchantBankDetails(amountLabel, getBankPaymentPurpose(reservedStg), reservedStg)}
            />
          </div>
        ) : null}
        {!COMPILE_BANK_IBAN_ONLY && !singleBankCheckout && enabledPaymentMethods.includes("novapay_card") && isNovapayCheckoutDegradedStaging() ? (
          <div
            className="sv2-alert"
            style={{ marginBottom: 12 }}
            data-testid="sv2-novapay-degraded-banner"
          >
            <strong>{t(lang, "payment_novapay_degraded_title")}</strong>
            <div>{t(lang, "payment_novapay_degraded_state")}</div>
            <p className="sv2-muted" style={{ marginTop: 6, marginBottom: 0 }}>
              {t(lang, "payment_novapay_degraded_body")}
            </p>
          </div>
        ) : !COMPILE_BANK_IBAN_ONLY && !singleBankCheckout && enabledPaymentMethods.includes("novapay_card") && isNovapayNotLiveUi() ? (
          <div
            className="sv2-alert"
            style={{ marginBottom: 12 }}
            data-testid="sv2-novapay-not-live-banner"
          >
            <strong>{t(lang, "payment_novapay_not_live_title")}</strong>
            <div>{t(lang, "payment_novapay_not_live_state")}</div>
            <p className="sv2-muted" style={{ marginTop: 6, marginBottom: 0 }}>
              {t(lang, "payment_novapay_not_live_body")}
            </p>
          </div>
        ) : null}
        {!COMPILE_BANK_IBAN_ONLY && !singleBankCheckout && paymentMethod === "novapay_card" &&
        !isNovapayCheckoutDegradedStaging() &&
        !isNovapayNotLiveUi() ? (
          <div style={{ marginBottom: 8 }}>
            <NovapaySandboxBadge lang={lang} testId="sv2-checkout-sandbox-badge" />
          </div>
        ) : null}
        {!COMPILE_BANK_IBAN_ONLY && !singleBankCheckout && showPaymentRadios ? (
        <div className="sv2-pay-radio-group" role="radiogroup" aria-label={t(lang, "choose_payment")}>
          {enabledPaymentMethods.includes("novapay_card") ? (
          <label
            className={`sv2-pay-radio${paymentMethod === "novapay_card" ? " is-selected" : ""}${
              isNovapayCheckoutDegradedStaging() || isNovapayNotLiveUi() ? " is-disabled" : ""
            }`}
            data-testid="sv2-checkout-novapay"
            aria-disabled={
              isNovapayCheckoutDegradedStaging() || isNovapayNotLiveUi() ? true : undefined
            }
          >
            <input
              type="radio"
              name="sv2-pay-method"
              value="novapay_card"
              checked={paymentMethod === "novapay_card"}
              disabled={isNovapayCheckoutDegradedStaging() || isNovapayNotLiveUi()}
              onChange={() => {
                if (!isNovapayCheckoutDegradedStaging() && !isNovapayNotLiveUi()) {
                  setPaymentMethod("novapay_card");
                }
              }}
            />
            <span className="sv2-pay-radio-body">
              <span className="sv2-pay-radio-title-row">
                <NovapayPaymentOptionLabel lang={lang} />
                {isNovapayCheckoutDegradedStaging() ? (
                  <span className="sv2-pay-badge" data-testid="sv2-novapay-degraded-badge">
                    {t(lang, "payment_novapay_degraded_state")}
                  </span>
                ) : isNovapayNotLiveUi() ? (
                  <span className="sv2-pay-badge" data-testid="sv2-novapay-not-live-badge">
                    {t(lang, "payment_novapay_not_live_state")}
                  </span>
                ) : (
                  <span className="sv2-pay-badge">{lang === "en" ? "Recommended" : "Рекомендовано"}</span>
                )}
              </span>
              {isNovapayCheckoutDegradedStaging() ? (
                <span className="sv2-muted" data-testid="sv2-novapay-degraded-hint">
                  {t(lang, "payment_novapay_degraded_body")}
                </span>
              ) : isNovapayNotLiveUi() ? (
                <span className="sv2-muted" data-testid="sv2-novapay-not-live-hint">
                  {t(lang, "payment_novapay_not_live_body")}
                </span>
              ) : null}
            </span>
          </label>
          ) : null}
          {paymentMethod === "novapay_card" &&
          !isNovapayCheckoutDegradedStaging() &&
          !isNovapayNotLiveUi() ? (
            <CardBrandTrustBlock lang={lang} compact testId="sv2-checkout-card-trust" />
          ) : null}

          {enabledPaymentMethods.includes("bank_transfer_fop") ? (
          <label
            className={`sv2-pay-radio${paymentMethod === "bank_transfer_fop" ? " is-selected" : ""}`}
            data-testid="sv2-checkout-fop"
          >
            <input
              type="radio"
              name="sv2-pay-method"
              value="bank_transfer_fop"
              checked={paymentMethod === "bank_transfer_fop"}
              onChange={() => setPaymentMethod("bank_transfer_fop")}
            />
            <span className="sv2-pay-radio-body">
              <strong>{t(lang, "fop_payment_option")}</strong>
              <span className="sv2-muted">{t(lang, "bank_fixed_method_hint")}</span>
            </span>
          </label>
          ) : null}

          {enabledPaymentMethods.includes("nova_poshta_cod") ? (
          <label
            className={`sv2-pay-radio${paymentMethod === "nova_poshta_cod" ? " is-selected" : ""}`}
            data-testid="sv2-checkout-cod"
          >
            <input
              type="radio"
              name="sv2-pay-method"
              value="nova_poshta_cod"
              checked={paymentMethod === "nova_poshta_cod"}
              onChange={() => setPaymentMethod("nova_poshta_cod")}
            />
            <span className="sv2-pay-radio-body">
              <strong>{t(lang, "cod_payment_option")}</strong>
              <span className="sv2-muted">
                {lang === "en"
                  ? "Pay on receipt under carrier terms."
                  : "Оплата під час отримання за умовами перевізника."}
              </span>
            </span>
          </label>
          ) : null}
        </div>
        ) : !COMPILE_BANK_IBAN_ONLY && !singleBankCheckout ? (
          <div className="sv2-info-card" data-testid="sv2-checkout-fixed-bank" style={{ marginTop: 4 }}>
            <strong>{t(lang, "bank_method_title")}</strong>
            <p className="sv2-muted" style={{ margin: "6px 0 0" }}>
              {t(lang, "bank_fixed_method_hint")}
            </p>
          </div>
        ) : null}
        {show("paymentMethod") ? <div className="sv2-field-err">{show("paymentMethod")}</div> : null}
        {(singleBankCheckout || paymentMethod === "bank_transfer_fop") && COMPILE_BANK_IBAN_ONLY && !TWO_FINAL_ACTION_CTAS ? (
          <div
            className="sv2-timing-actions"
            role="radiogroup"
            aria-label={t(lang, "payment_timing_section")}
            data-testid="sv2-checkout-payment-timing"
          >
            <button
              type="button"
              role="radio"
              className={`sv2-timing-option${paymentTiming === "immediate" ? " is-selected" : ""}`}
              data-testid="sv2-checkout-timing-immediate"
              data-selected={paymentTiming === "immediate" ? "true" : "false"}
              aria-checked={paymentTiming === "immediate"}
              onClick={() => {
                setPaymentTiming("immediate");
                emitEvent("checkout_payment_timing_selected", { payment_timing: "immediate" });
              }}
            >
              <span className="sv2-timing-radio sv2-timing-check" aria-hidden="true">
                {paymentTiming === "immediate" ? "✓" : "○"}
              </span>
              <span className="sv2-timing-option-body">
                <span className="sv2-timing-option-label">{t(lang, "pay_after_create")}</span>
                <span className="sv2-timing-option-sub">{t(lang, "pay_after_create_subcopy")}</span>
              </span>
            </button>
            <button
              type="button"
              role="radio"
              className={`sv2-timing-option${paymentTiming === "after_manager_confirmation" ? " is-selected" : ""}`}
              data-testid="sv2-checkout-timing-confirm-first"
              data-selected={paymentTiming === "after_manager_confirmation" ? "true" : "false"}
              aria-checked={paymentTiming === "after_manager_confirmation"}
              onClick={() => {
                setPaymentTiming("after_manager_confirmation");
                emitEvent("checkout_payment_timing_selected", {
                  payment_timing: "after_manager_confirmation",
                });
              }}
            >
              <span className="sv2-timing-radio sv2-timing-check" aria-hidden="true">
                {paymentTiming === "after_manager_confirmation" ? "✓" : "○"}
              </span>
              <span className="sv2-timing-option-body">
                <span className="sv2-timing-option-label">{t(lang, "confirm_availability_first")}</span>
                <span className="sv2-timing-option-sub">{t(lang, "confirm_availability_first_subcopy")}</span>
              </span>
            </button>
          </div>
        ) : null}
      </section>

      <section className="sv2-express-card" data-testid="sv2-express-comment">
        {!noteExpanded && !note.trim() ? (
          <button
            type="button"
            className="sv2-cta sv2-secondary"
            data-testid="sv2-checkout-add-comment"
            onClick={() => setNoteExpanded(true)}
            style={{ width: "100%" }}
          >
            {t(lang, "add_order_comment")}
          </button>
        ) : (
          <>
            <h3>{t(lang, "note")}</h3>
            {isActiveReorderSession(peekReorderSession()) ? (
              <p className="sv2-muted" style={{ margin: "0 0 8px", fontSize: 13 }}>
                {t(lang, "reorder_comment_hint")}
              </p>
            ) : null}
            <IsolatedFormField
              id="sv2-note"
              label={t(lang, "optional")}
              value={note}
              onCommit={(v) => {
                setNote(v);
                if (v.trim()) setNoteExpanded(true);
              }}
              commitFilter={(raw) => raw.slice(0, 300)}
              onBlurExtra={() => markTouched("note")}
              multiline
              rows={3}
              maxLength={300}
              registerFlush={registerFieldFlush}
              testId="sv2-note-input"
            />
          </>
        )}
      </section>

      <div className="sv2-checkout-cta-wrap" data-testid="sv2-checkout-cta-wrap">
        {onOpenLegalOffer && onOpenLegalPrivacy ? (
          <CheckoutLegalAcceptance
            lang={lang}
            onOpenOffer={onOpenLegalOffer}
            onOpenPrivacy={onOpenLegalPrivacy}
          />
        ) : null}
        {(singleBankCheckout || paymentMethod === "bank_transfer_fop") && COMPILE_BANK_IBAN_ONLY && TWO_FINAL_ACTION_CTAS ? (
          <>
            {managerConfirmOnly ? (
              <div
                className="sv2-alert sv2-alert-warn"
                data-testid="sv2-checkout-stock-review-warning"
                role="status"
              >
                <strong>{t(lang, "checkout_stock_review_title")}</strong>
                <div>{t(lang, "checkout_stock_review_body")}</div>
              </div>
            ) : null}
            <div className="sv2-timing-actions sv2-checkout-final-actions" data-testid="sv2-checkout-final-actions">
            {!managerConfirmOnly ? (
            <button
              type="button"
              className="sv2-timing-option is-selected sv2-checkout-action-cta"
              data-testid="sv2-checkout-cta-bank-pay"
              disabled={!readiness.ready || submitting}
              aria-busy={submitting ? true : undefined}
              onClick={() => {
                emitEvent("checkout_pay_bank_action_clicked", { amount_digits: totalDigits });
                emitEvent("cta_click", { target_key: "checkout.pay_bank", amount_digits: totalDigits });
                void submit("immediate");
              }}
            >
              <span className="sv2-timing-radio" aria-hidden="true">
                <StoreV2Icon name="bank" size={22} />
              </span>
              <span className="sv2-timing-option-body">
                <span className="sv2-timing-option-label">{t(lang, "checkout_cta_bank_title")}</span>
                <span className="sv2-timing-option-sub">
                  {submitting && paymentTiming === "immediate"
                    ? t(lang, "creating_order")
                    : t(lang, "checkout_cta_bank_amount", { TOTAL: totalDigits })}
                </span>
              </span>
            </button>
            ) : null}
            <button
              type="button"
              className="sv2-timing-option is-selected sv2-checkout-action-cta"
              data-testid={
                managerConfirmOnly
                  ? "sv2-checkout-cta-manager-review"
                  : "sv2-checkout-cta-confirm-availability"
              }
              disabled={!readiness.ready || submitting}
              aria-busy={submitting ? true : undefined}
              onClick={() => {
                emitEvent("checkout_confirm_availability_action_clicked", { amount_digits: totalDigits });
                emitEvent("cta_click", {
                  target_key: managerConfirmOnly ? "checkout.manager_review" : "checkout.confirm_availability",
                  amount_digits: totalDigits,
                });
                void submit("after_manager_confirmation");
              }}
            >
              <span className="sv2-timing-radio" aria-hidden="true">
                <StoreV2Icon name="check" size={22} />
              </span>
              <span className="sv2-timing-option-body">
                <span className="sv2-timing-option-label">
                  {managerConfirmOnly
                    ? t(lang, "checkout_cta_manager_review_title")
                    : t(lang, "checkout_cta_confirm_title")}
                </span>
                <span className="sv2-timing-option-sub">
                  {submitting && paymentTiming === "after_manager_confirmation"
                    ? t(lang, "creating_order")
                    : t(lang, "checkout_cta_confirm_amount", { TOTAL: totalDigits })}
                </span>
              </span>
            </button>
          </div>
          </>
        ) : (
          <PrimaryCommerceAction
            testId="sv2-express-submit"
            disabled={!readiness.ready}
            loading={submitting}
            onClick={() => void submit()}
          >
            {ctaLabel}
          </PrimaryCommerceAction>
        )}
        {!readiness.ready && readiness.blockers[0] ? (
          <p
            className="sv2-muted"
            style={{ marginTop: 8, fontSize: 13 }}
            data-testid="sv2-checkout-blocker"
          >
            {readiness.blockers[0]}
          </p>
        ) : null}
      </div>
      {paymentMethod === "novapay_card" && !COMPILE_BANK_IBAN_ONLY && !singleBankCheckout && prepared?.paymentUrl ? (
        <p className="sv2-muted" style={{ marginTop: 8, fontSize: 12 }} data-testid="sv2-handoff-ready">
          {lang === "en" ? "Secure payment is ready — one tap opens the payment page." : "Захищена оплата готова — одне натискання відкриє сторінку оплати."}
        </p>
      ) : null}

      {error ? (
        <div className="sv2-alert sv2-alert-err" style={{ marginTop: 12 }} data-sv2-checkout-error="true">
          <div>{error}</div>
          {retryable ? (
            <button
              type="button"
              className="sv2-cta sv2-secondary"
              style={{ marginTop: 10 }}
              disabled={submitting}
              data-sv2-checkout-retry="true"
              onClick={() => void submit()}
            >
              {t(lang, "try_again")}
            </button>
          ) : null}
          <div style={{ marginTop: 10 }}>
            <ReportErrorCta lang={lang} origin="checkout_error" />
          </div>
        </div>
      ) : null}
      <div className="sv2-muted" style={{ marginTop: 12, fontSize: 11 }}>
        {singleBankCheckout
          ? `${STORE_INFO.merchantName}`
          : `${STORE_INFO.merchantName} · карткові дані не зберігаються`}
      </div>
    </div>
    </CommercePageShell>
  );
}
